import { useState, useEffect, useMemo } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Modal, Spinner } from './ui';
import { invoiceApi } from '../api';

interface SendInvoiceDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	appointmentId?: number;
	invoiceId?: number;
	customerName: string;
	serviceName: string;
	servicePrice?: number;
	deposit?: number;
	sizeAndLength?: string;
	appointmentDate?: string;
	staffName?: string;
	customerEmail?: string;
	customerPhone?: string;
	defaultDiscount?: number;
	defaultMiscCharges?: number;
	defaultMiscDescription?: string;
}

const formatCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const SendInvoiceDialog = ({
	isOpen,
	onClose,
	onSuccess,
	appointmentId,
	invoiceId,
	customerName,
	serviceName,
	servicePrice = 0,
	deposit = 0,
	sizeAndLength,
	appointmentDate,
	staffName,
	customerEmail,
	customerPhone,
	defaultDiscount = 0,
	defaultMiscCharges = 0,
	defaultMiscDescription = '',
}: SendInvoiceDialogProps) => {
	const [discount, setDiscount] = useState(0);
	const [miscCharges, setMiscCharges] = useState(0);
	const [miscDescription, setMiscDescription] = useState('');
	const [sendEmail, setSendEmail] = useState(false);
	const [sendSms, setSendSms] = useState(false);
	const [note, setNote] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Reset form when dialog opens
	useEffect(() => {
		if (isOpen) {
			setDiscount(defaultDiscount);
			setMiscCharges(defaultMiscCharges);
			setMiscDescription(defaultMiscDescription);
			setSendEmail(!!customerEmail);
			setSendSms(!customerEmail && !!customerPhone);
			setNote('');
			setIsSubmitting(false);
		}
	}, [isOpen, defaultDiscount, defaultMiscCharges, defaultMiscDescription, customerEmail, customerPhone]);

	const balanceDue = useMemo(() => {
		return Math.max(0, servicePrice - deposit - discount + miscCharges);
	}, [servicePrice, deposit, discount, miscCharges]);

	const invoiceNumber = useMemo(() => {
		if (invoiceId) return `INV-${String(invoiceId).padStart(4, '0')}`;
		if (appointmentId) return `INV-${String(appointmentId).padStart(4, '0')}-${new Date().getFullYear()}`;
		return '';
	}, [appointmentId, invoiceId]);

	const maxDiscount = Math.max(0, servicePrice - deposit);

	const handleSubmit = async () => {
		if (!sendEmail && !sendSms) {
			toast.error('Please select at least one delivery method (Email or SMS).');
			return;
		}

		if (servicePrice > 0 && discount > maxDiscount) {
			toast.error(`Discount cannot exceed the service amount ($${maxDiscount.toFixed(2)}).`);
			return;
		}

		setIsSubmitting(true);
		try {
			const channels: string[] = [];
			if (sendEmail) channels.push('email');
			if (sendSms) channels.push('sms');

			await invoiceApi.send({
				...(appointmentId ? { appointmentId } : {}),
				...(invoiceId ? { invoiceId } : {}),
				discount,
				miscCharges,
				miscDescription,
				sendVia: channels.join(','),
			});

			const channelText = channels.map(c => (c === 'email' ? 'Email' : 'SMS')).join(' and ');
			toast.success(
				invoiceId
					? `Invoice resent via ${channelText}`
					: `Invoice sent to ${customerName} via ${channelText}`
			);
			onSuccess();
			onClose();
		} catch {
			toast.error('Failed to send invoice. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} title="Send Invoice" size="md">
			<div>
				{/* Invoice Preview Card */}
				<div className="rounded-xl overflow-hidden mb-5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
					{/* Gradient Header */}
					<div
						className="px-5 py-4 flex justify-between items-center text-white"
						style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
					>
						<h3 className="text-base font-bold">Beauty Business Portal</h3>
						<span className="text-[13px] opacity-90">{invoiceNumber}</span>
					</div>

					{/* Body Rows */}
					<div className="px-5 py-4">
						<InvoiceRow label="Client" value={customerName} />
						<InvoiceRow label="Service" value={serviceName} />
						{sizeAndLength && <InvoiceRow label="Details" value={sizeAndLength} />}
						{appointmentDate && <InvoiceRow label="Date" value={appointmentDate} />}
						{staffName && <InvoiceRow label="Stylist" value={staffName} />}
						{servicePrice > 0 && (
							<InvoiceRow label="Service Price" value={formatCurrency(servicePrice)} />
						)}
						{deposit > 0 && (
							<InvoiceRow
								label="Deposit Paid"
								value={`-${formatCurrency(deposit)}`}
								valueColor="text-emerald-500"
							/>
						)}

						{/* Editable: Discount */}
						<div className="flex justify-between items-center py-2.5 border-t border-gray-200 dark:border-gray-600 text-sm">
							<span className="text-gray-500 dark:text-gray-400">Discount</span>
							<div className="flex items-center gap-1">
								<span className="font-semibold text-gray-900 dark:text-white text-sm">-$</span>
								<input
									type="number"
									min="0"
									max={servicePrice > 0 ? maxDiscount : undefined}
									step="0.01"
									value={discount || ''}
									onChange={e => {
										const val = parseFloat(e.target.value) || 0;
										setDiscount(servicePrice > 0 ? Math.min(val, maxDiscount) : val);
									}}
									placeholder="0.00"
									className="w-20 px-2.5 py-1.5 rounded-md text-sm font-semibold text-right border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
								/>
							</div>
						</div>

						{/* Editable: Misc */}
						<div className="flex justify-between items-center py-2.5 border-t border-gray-200 dark:border-gray-600 text-sm">
							<span className="text-gray-500 dark:text-gray-400">Misc</span>
							<div className="flex items-center gap-1">
								<span className="font-semibold text-gray-900 dark:text-white text-sm">+$</span>
								<input
									type="number"
									min="0"
									step="0.01"
									value={miscCharges || ''}
									onChange={e => setMiscCharges(parseFloat(e.target.value) || 0)}
									placeholder="0.00"
									className="w-20 px-2.5 py-1.5 rounded-md text-sm font-semibold text-right border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
								/>
							</div>
						</div>
					</div>

					{/* Balance Due Total */}
					{servicePrice > 0 && (
						<div className="flex justify-between items-center px-5 py-3.5 text-base font-bold border-t-2 border-indigo-500 bg-indigo-50 dark:bg-indigo-900/15 text-indigo-600 dark:text-blue-300">
							<span>Balance Due</span>
							<span>{formatCurrency(balanceDue)}</span>
						</div>
					)}
				</div>

				{/* Send Via - Checkbox-based multi-select */}
				<div className="mb-4">
					<label className="block text-[13px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
						Send via
					</label>
					<div className="flex flex-col gap-3">
						{customerEmail && (
							<label
								onClick={() => setSendEmail(prev => !prev)}
								className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
									sendEmail
										? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/15'
										: 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-indigo-400'
								}`}
							>
								<input
									type="checkbox"
									checked={sendEmail}
									onChange={() => {}}
									className="w-[18px] h-[18px] accent-indigo-500 cursor-pointer"
								/>
								<span className="text-lg">📧</span>
								<div className="flex-1">
									<strong className="block text-sm text-gray-900 dark:text-white">
										Email
									</strong>
									<span className="text-xs text-gray-400 dark:text-gray-500">
										{customerEmail}
									</span>
								</div>
							</label>
						)}
						{customerPhone && (
							<label
								onClick={() => setSendSms(prev => !prev)}
								className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
									sendSms
										? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/15'
										: 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-indigo-400'
								}`}
							>
								<input
									type="checkbox"
									checked={sendSms}
									onChange={() => {}}
									className="w-[18px] h-[18px] accent-indigo-500 cursor-pointer"
								/>
								<span className="text-lg">💬</span>
								<div className="flex-1">
									<strong className="block text-sm text-gray-900 dark:text-white">
										SMS
									</strong>
									<span className="text-xs text-gray-400 dark:text-gray-500">
										{customerPhone}
									</span>
								</div>
							</label>
						)}
						{/* Fallback when no contact info available */}
						{!customerEmail && !customerPhone && (
							<div className="flex gap-3">
								<label
									onClick={() => setSendEmail(prev => !prev)}
									className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
										sendEmail
											? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/15 text-indigo-600 dark:text-indigo-400'
											: 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-400'
									}`}
								>
									<input
										type="checkbox"
										checked={sendEmail}
										onChange={() => {}}
										className="w-[18px] h-[18px] accent-indigo-500 cursor-pointer"
									/>
									<span className="text-sm font-semibold">Email</span>
								</label>
								<label
									onClick={() => setSendSms(prev => !prev)}
									className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
										sendSms
											? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/15 text-indigo-600 dark:text-indigo-400'
											: 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-400'
									}`}
								>
									<input
										type="checkbox"
										checked={sendSms}
										onChange={() => {}}
										className="w-[18px] h-[18px] accent-indigo-500 cursor-pointer"
									/>
									<span className="text-sm font-semibold">SMS</span>
								</label>
							</div>
						)}
					</div>
				</div>

				{/* Note (optional) */}
				<div className="mb-5">
					<label className="block text-[13px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
						Add a note (optional)
					</label>
					<textarea
						value={note}
						onChange={e => setNote(e.target.value)}
						placeholder="e.g. Thank you for your visit! Payment is due upon arrival."
						rows={3}
						className="w-full px-3.5 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 resize-y"
					/>
				</div>

				{/* Actions */}
				<div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					<button
						onClick={onClose}
						disabled={isSubmitting}
						className="flex-1 py-3 rounded-lg font-semibold text-sm text-gray-600 dark:text-gray-400 border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						onClick={handleSubmit}
						disabled={isSubmitting}
						className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm text-white transition-all hover:shadow-lg disabled:opacity-50"
						style={{
							background: 'linear-gradient(135deg, #667eea, #764ba2)',
						}}
					>
						{isSubmitting ? (
							<Spinner size="sm" />
						) : (
							<>
								<PaperAirplaneIcon className="w-4 h-4" />
								{invoiceId ? 'Resend Invoice' : 'Send Invoice'}
							</>
						)}
					</button>
				</div>
			</div>
		</Modal>
	);
};

// ─── Invoice Row ────────────────────────────────────────────────

const InvoiceRow = ({
	label,
	value,
	valueColor,
}: {
	label: string;
	value: string;
	valueColor?: string;
}) => (
	<div className="flex justify-between items-center py-2.5 border-t border-gray-200 dark:border-gray-600 first:border-t-0 text-sm">
		<span className="text-gray-500 dark:text-gray-400">{label}</span>
		<span className={`font-semibold ${valueColor || 'text-gray-900 dark:text-white'}`}>
			{value}
		</span>
	</div>
);

export default SendInvoiceDialog;
